import { Loading } from "@corecrm/ui";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useMe } from "./api/queries";
import { BillingOnly } from "./components/bits";
import { PortalShell } from "./components/PortalShell";
import { BudgetPage } from "./pages/Budget";
import { ClaudePage } from "./pages/Claude";
import { DeliverablePage } from "./pages/Deliverable";
import { FeedbackPage } from "./pages/Feedback";
import { HomePage } from "./pages/Home";
import { ImpersonatePage } from "./pages/Impersonate";
import { InvoicePage, InvoicesPage } from "./pages/Invoices";
import { JoinPage } from "./pages/Join";
import { NotFoundPage } from "./pages/NotFound";
import { PreviewPage } from "./pages/Preview";
import { ProjectPage } from "./pages/Project";
import { ProjectsPage } from "./pages/Projects";
import { QuestionsPage } from "./pages/Questions";
import { QuotePage } from "./pages/Quote";
import { QuotesPage } from "./pages/Quotes";
import { ForgotPasswordPage, SetPasswordPage, SignInLinkPage, SignInPage } from "./pages/SignIn";

/* Every path an email links to has a route here: /join/<token>, /sign-in, /sign-in/link?token=,
   /set-password?token=, /feedback/<token> (no sign-in needed), /quotes/<id>, /projects/<id> and
   /deliverables/<id> (backend services build them from portal_app_url). */
export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-in/link" element={<SignInLinkPage />} />
      <Route path="/impersonate" element={<ImpersonatePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/feedback/:token" element={<FeedbackPage />} />
      <Route path="/preview" element={<PreviewPage />} />
      <Route
        element={
          <RequireClient>
            <PortalShell />
          </RequireClient>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:id" element={<QuotePage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectPage />} />
        {/* Deep links to one bug or feature request: the project with that record open. */}
        <Route path="projects/:id/bugs/:bugId" element={<ProjectPage />} />
        <Route path="projects/:id/feature-requests/:requestId" element={<ProjectPage />} />
        <Route path="projects/:id/tests/:caseId" element={<ProjectPage />} />
        <Route path="deliverables/:id" element={<DeliverablePage />} />
        <Route path="questions" element={<QuestionsPage />} />
        <Route path="budget" element={<BudgetPage />} />
        <Route
          path="invoices"
          element={
            <BillingOnly>
              <InvoicesPage />
            </BillingOnly>
          }
        />
        <Route
          path="invoices/:id"
          element={
            <BillingOnly>
              <InvoicePage />
            </BillingOnly>
          }
        />
        <Route path="claude" element={<ClaudePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/** Signed-in pages: without a portal session, go to sign in and come back afterwards. */
function RequireClient({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <Loading />;
  if (!me.data) {
    const next = location.pathname + location.search;
    return <Navigate to={next === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}
